/**
 * Day 91a P2B — Buy Score weights proportional auto-rebalance pure function.
 *
 * Used by ParamsSidebar.tsx weights section on weight-input blur to redistribute
 * the delta between the user's new value and the prior 100-sum across the
 * remaining enabled is_available=true weights proportionally to their current
 * values. Per R-Lock-6 algorithm + R-Lock-7 scope + #DBN-Phase-0-1 reframe
 * (enhances pre-existing visual sum indicator rather than greenfield).
 *
 * Pure function — no React, no Supabase. Day 89 #DBN-λ reclassify candidate
 * per build prompt §6 P3 Block A (pure-function unit-test reclassify from
 * Block B substrate-and-RLS to Block A source-structure + math).
 *
 * Algorithm (R-Lock-6 proportional, no lock affordance V1):
 *   delta = total_excluding_changed_weight_target − current_total_excluding_changed
 *   for each remaining weight W:
 *     redistributed_W = W + (W / sum_of_remaining) × delta
 *     clamp to 0 (negative weights nonsensical at engine layer)
 *
 * Edge cases:
 *   - remaining keys empty → return {} (caller handles single-weight UX
 *     gracefully; no rebalance needed when only one weight exists)
 *   - delta = 0 → return current values unchanged
 *   - single remaining → absorbs full delta
 *   - all current values = 0 → equal distribution of delta
 *
 * Sum invariant: with delta = (100 − sum_after_user_input_excluding_changed),
 * the returned values sum to (sum_of_remaining + delta), which equals
 * (100 − changed_weight_new_value) — preserving global sum=100 invariant.
 *
 * Vitest coverage retired at Engine Editor V1 Day 3 P5 (day91aBuyScoreWeightsAdmin.test.ts
 * deleted with BuyScoreWeights admin page deprecation; rebalance logic remains
 * load-bearing — re-cohort at next Engine Editor test-coverage day per BACKLOG
 * follow-up).
 */

/**
 * Redistribute `delta` across `currentValues` proportionally to current values.
 * Returns a NEW values map (same keys as input); does not mutate input.
 *
 * @param currentValues - keyed numeric values to redistribute across
 * @param delta - amount to distribute (positive = increase others; negative = decrease)
 * @returns new values map with delta absorbed proportionally; values clamped ≥ 0
 */
export function rebalanceProportional(
  currentValues: Record<string, number>,
  delta: number
): Record<string, number> {
  const keys = Object.keys(currentValues);

  // Empty remaining → nothing to redistribute
  if (keys.length === 0) return {};

  // Zero delta → return current values unchanged
  if (delta === 0) {
    return Object.fromEntries(keys.map((k) => [k, currentValues[k]]));
  }

  // Single remaining → absorbs full delta (clamp to 0)
  if (keys.length === 1) {
    const k = keys[0];
    return { [k]: Math.max(0, currentValues[k] + delta) };
  }

  const total = keys.reduce((sum, k) => sum + currentValues[k], 0);

  // All remaining = 0 → equal distribution of delta
  if (total === 0) {
    const share = delta / keys.length;
    return Object.fromEntries(keys.map((k) => [k, Math.max(0, share)]));
  }

  // Standard proportional redistribution; preserves ratio between current values
  return Object.fromEntries(
    keys.map((k) => {
      const proportionalShare = (currentValues[k] / total) * delta;
      return [k, Math.max(0, currentValues[k] + proportionalShare)];
    })
  );
}
